import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { Input, Button, FormField } from '../components/ui'
import ConfirmationModal from '../components/modals/ConfirmationModal'
import { getFieldIcon } from '../utils/formIcons'
import { transportadora } from '../assets/data/arrays'
import {
  UserCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline'
import {
  getOrderByGuia,
  type OrderDetailFromGuia,
  type LineItemFromGuia,
  type KitComponent
} from '../services/orders/getOrderByGuiaService'
import { updateOrderStatus } from '../services/orders/updateOrderStatusService'

const DEBOUNCE_MS = 500
const MIN_GUIA_LENGTH = 5

type ItemEstado = 'pendiente' | 'empacado' | 'incidencia'

const ESTADO_LABELS: Record<ItemEstado, string> = {
  pendiente: 'Pendiente',
  empacado: 'Empacado',
  incidencia: 'Incidencia'
}

const ESTADO_CLASSES: Record<ItemEstado, string> = {
  pendiente: 'bg-gray-100 text-gray-800',
  empacado: 'bg-green-100 text-green-800',
  incidencia: 'bg-red-100 text-red-800'
}

const ESTADO_CYCLE: ItemEstado[] = ['pendiente', 'empacado', 'incidencia']

function getTransportadoraLabel(value: string): string {
  const item = transportadora.find((t: { value: string }) => t.value === value)
  return item ? (item as { label: string }).label : value
}

function isKit(item: LineItemFromGuia): boolean {
  const hasCombo = item.is_combo === 1 || item.is_combo === true
  const hasComponents = Array.isArray(item.components) && item.components.length > 0
  return Boolean(hasCombo && hasComponents)
}

function buildInitialItemStates(lineItems: LineItemFromGuia[]): Record<string, ItemEstado> {
  const initial: Record<string, ItemEstado> = {}
  lineItems.forEach((item, idx) => {
    initial[`main-${idx}`] = 'pendiente'
    if (isKit(item) && item.components) {
      item.components.forEach((_, cIdx) => {
        initial[`comp-${idx}-${cIdx}`] = 'pendiente'
      })
    }
  })
  return initial
}

function aggregateKitState(componentStates: ItemEstado[]): ItemEstado {
  const hasPendiente = componentStates.some((s) => s === 'pendiente')
  const hasIncidencia = componentStates.some((s) => s === 'incidencia')
  if (hasPendiente) return 'pendiente'
  if (hasIncidencia) return 'incidencia'
  return 'empacado'
}

type RenderRow =
  | { kind: 'main'; lineIndex: number; item: LineItemFromGuia }
  | { kind: 'component'; lineIndex: number; componentIndex: number; component: KitComponent }

const DespachosBodega: React.FC = () => {
  const [guiaInput, setGuiaInput] = useState('')
  const [debouncedGuia, setDebouncedGuia] = useState('')
  const [orderDetail, setOrderDetail] = useState<OrderDetailFromGuia | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [itemStates, setItemStates] = useState<Record<string, ItemEstado>>({})
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [showEmpacadoModal, setShowEmpacadoModal] = useState(false)
  const [showIncidenciaModal, setShowIncidenciaModal] = useState(false)

  // Debounce: solo actualizar debouncedGuia cuando guiaInput tiene más de 4 caracteres
  useEffect(() => {
    const trimmed = guiaInput.trim()
    if (trimmed.length <= 4) {
      setDebouncedGuia('')
      setOrderDetail(null)
      setError(null)
      setItemStates({})
      return
    }
    const timer = setTimeout(() => setDebouncedGuia(trimmed), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [guiaInput])

  // Búsqueda automática cuando debouncedGuia tiene más de 4 caracteres
  useEffect(() => {
    if (!debouncedGuia || debouncedGuia.length < MIN_GUIA_LENGTH) return

    let cancelled = false
    setLoading(true)
    setError(null)

    getOrderByGuia(debouncedGuia)
      .then((order) => {
        if (cancelled) return
        if (order) {
          setOrderDetail(order)
          const lineItems = order.lineItems ?? []
          setItemStates(buildInitialItemStates(lineItems))
        } else {
          setOrderDetail(null)
          setError('Guía no encontrada')
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Error buscando por guía:', err)
        setError('Error al buscar la guía')
        setOrderDetail(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedGuia])

  const lineItems = useMemo(() => orderDetail?.lineItems ?? [], [orderDetail?.lineItems])

  const tableRows = useMemo((): RenderRow[] => {
    const rows: RenderRow[] = []
    lineItems.forEach((item, lineIndex) => {
      rows.push({ kind: 'main', lineIndex, item })
      if (isKit(item) && item.components) {
        item.components.forEach((component, componentIndex) => {
          rows.push({ kind: 'component', lineIndex, componentIndex, component })
        })
      }
    })
    return rows
  }, [lineItems])

  const toggleEstado = useCallback(
    (key: string) => {
      setItemStates((prev) => {
        const current = prev[key] ?? 'pendiente'
        const idx = ESTADO_CYCLE.indexOf(current)
        const next = ESTADO_CYCLE[(idx + 1) % ESTADO_CYCLE.length]
        const nextState = { ...prev, [key]: next }
        if (key.startsWith('comp-')) {
          const match = key.match(/^comp-(\d+)-/)
          if (match) {
            const kitIndex = parseInt(match[1], 10)
            const item = lineItems[kitIndex]
            if (item && isKit(item) && item.components) {
              const compStates = item.components.map(
                (_, cIdx) => nextState[`comp-${kitIndex}-${cIdx}`] ?? 'pendiente'
              )
              nextState[`main-${kitIndex}`] = aggregateKitState(compStates)
            }
          }
        }
        return nextState
      })
    },
    [lineItems]
  )

  const setAllItemStates = useCallback(
    (estado: ItemEstado) => {
      setItemStates((prev) => {
        const next = { ...prev }
        lineItems.forEach((item, idx) => {
          next[`main-${idx}`] = estado
          if (isKit(item) && item.components) {
            item.components.forEach((_, cIdx) => {
              next[`comp-${idx}-${cIdx}`] = estado
            })
          }
        })
        return next
      })
    },
    [lineItems]
  )

  const handleConfirmEmpacado = useCallback(async () => {
    if (!orderDetail?.id) return
    setUpdatingStatus(true)
    try {
      await updateOrderStatus(orderDetail.id, { status: 'empacado' })
      setAllItemStates('empacado')
      toast.success('Pedido actualizado a Empacado', { position: 'top-center' })
      setGuiaInput('')
    } catch (err) {
      console.error('Error actualizando estado:', err)
      toast.error('No se pudo actualizar el estado del pedido', { position: 'top-center' })
    } finally {
      setUpdatingStatus(false)
      setShowEmpacadoModal(false)
    }
  }, [orderDetail?.id, setAllItemStates])

  const handleConfirmIncidencia = useCallback(async () => {
    if (!orderDetail?.id) return
    setUpdatingStatus(true)
    try {
      await updateOrderStatus(orderDetail.id, { status: 'incidencia' })
      setAllItemStates('incidencia')
      toast.success('Pedido marcado con incidencia', { position: 'top-center' })
      setGuiaInput('')
    } catch (err) {
      console.error('Error actualizando estado:', err)
      toast.error('No se pudo actualizar el estado del pedido', { position: 'top-center' })
    } finally {
      setUpdatingStatus(false)
      setShowIncidenciaModal(false)
    }
  }, [orderDetail?.id, setAllItemStates])

  const hasAnyIncidencia = useMemo(
    () => Object.values(itemStates).some((estado) => estado === 'incidencia'),
    [itemStates]
  )
  const hasAnyEmpacado = useMemo(
    () => Object.values(itemStates).some((estado) => estado === 'empacado'),
    [itemStates]
  )
  const allEmpacado = useMemo(() => {
    const values = Object.values(itemStates)
    return values.length > 0 && values.every((estado) => estado === 'empacado')
  }, [itemStates])

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Buscar por # de guía */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h3 className="text-lg mb-4">Buscar por # de guía</h3>
          <FormField
            label="Número de guía"
            helpText="La búsqueda se realiza automáticamente al ingresar más de 4 caracteres"
            icon={getFieldIcon('guia')}
          >
            <Input
              type="text"
              value={guiaInput}
              onChange={(e) => setGuiaInput(e.target.value)}
              placeholder="Ingrese el número de guía"
              aria-label="Número de guía"
              disabled={loading}
            />
          </FormField>
          {loading && (
            <p className="text-sm text-gray-500 mt-2">Buscando...</p>
          )}
          {error && (
            <p className="text-sm text-red-600 mt-2" role="alert">{error}</p>
          )}
          {guiaInput.trim().length > 0 && guiaInput.trim().length <= 4 && (
            <p className="text-sm text-gray-500 mt-2">Ingrese al menos 5 caracteres para buscar</p>
          )}
        </div>

        {orderDetail && (
          <>
            {/* Datos del pedido */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <FormField label="Número de pedido" icon={getFieldIcon('documento')}>
                  <Input
                    readOnly
                    value={orderDetail.id ?? orderDetail.origin_order_id ?? ''}
                    aria-label="Número de pedido"
                  />
                </FormField>
                <FormField label="Número de guía" icon={getFieldIcon('guia')}>
                  <Input
                    readOnly
                    value={orderDetail.guia ?? ''}
                    aria-label="Número de guía"
                  />
                </FormField>
                <FormField label="Transportadora" icon={getFieldIcon('transportadora')}>
                  <Input
                    readOnly
                    value={getTransportadoraLabel(orderDetail.extraDetails?.transportadora ?? '')}
                    aria-label="Transportadora"
                  />
                </FormField>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <UserCircleIcon className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                <span>
                  Cliente: {[orderDetail.customer?.first_name, orderDetail.customer?.last_name].filter(Boolean).join(' ') || '—'}
                </span>
              </div>
            </div>

            {/* Detalle de productos */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <ArchiveBoxIcon className="h-5 w-5" aria-hidden />
                Detalle de Productos
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Producto
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cantidad
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tableRows.map((row) => {
                      if (row.kind === 'main') {
                        const { lineIndex, item } = row
                        const stateKey = `main-${lineIndex}`
                        const estado = itemStates[stateKey] ?? 'pendiente'
                        const isKitRow = isKit(item)
                        return (
                          <tr key={stateKey}>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.product_id}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.product_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.quantity}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${ESTADO_CLASSES[estado]} ${isKitRow ? 'cursor-default' : 'cursor-pointer transition-colors hover:opacity-90'}`}
                                role={isKitRow ? undefined : 'button'}
                                onClick={isKitRow ? undefined : () => toggleEstado(stateKey)}
                                onKeyDown={
                                  isKitRow
                                    ? undefined
                                    : (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault()
                                          toggleEstado(stateKey)
                                        }
                                      }
                                }
                                tabIndex={isKitRow ? undefined : 0}
                                aria-label={
                                  isKitRow
                                    ? `Estado ${ESTADO_LABELS[estado]}`
                                    : `Estado ${ESTADO_LABELS[estado]}. Clic para cambiar`
                                }
                              >
                                {ESTADO_LABELS[estado]}
                              </span>
                            </td>
                          </tr>
                        )
                      }
                      const { lineIndex, componentIndex, component } = row
                      const stateKey = `comp-${lineIndex}-${componentIndex}`
                      const estado = itemStates[stateKey] ?? 'pendiente'
                      const parentQuantity = Number(lineItems[lineIndex]?.quantity ?? 1)
                      const componentQuantity = Number(component.quantity ?? 0)
                      const totalComponentQuantity = componentQuantity * (Number.isNaN(parentQuantity) ? 1 : parentQuantity)
                      return (
                        <tr key={stateKey} className="bg-gray-50/50">
                          <td className="pl-8 pr-4 py-2 text-sm text-gray-700">
                            {component.sku}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">{component.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {totalComponentQuantity}
                          </td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => toggleEstado(stateKey)}
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors hover:opacity-90 ${ESTADO_CLASSES[estado]}`}
                              aria-label={`Estado ${ESTADO_LABELS[estado]}. Clic para cambiar`}
                            >
                              {ESTADO_LABELS[estado]}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex flex-col items-end gap-2">
              {orderDetail?.status !== 'picking_started' && (
                <p className="text-xs text-gray-500">
                  El pedido debe estar en estado Picking Iniciado para cambiar a Empacado o marcar Incidencia.
                </p>
              )}
              <div className="flex gap-3">
              <Button
                variant="success"
                leftIcon={CheckCircleIcon}
                onClick={() => setShowEmpacadoModal(true)}
                isLoading={updatingStatus}
                disabled={
                  updatingStatus ||
                  orderDetail?.status !== 'picking_started' ||
                  hasAnyIncidencia ||
                  !allEmpacado
                }
                aria-label="Marcar todo como Empacado"
              >
                Empacado
              </Button>
              <Button
                variant="danger"
                leftIcon={ExclamationTriangleIcon}
                onClick={() => setShowIncidenciaModal(true)}
                isLoading={updatingStatus}
                disabled={
                  updatingStatus ||
                  orderDetail?.status !== 'picking_started' ||
                  (hasAnyEmpacado && !hasAnyIncidencia)
                }
                aria-label="Marcar incidencia"
              >
                Incidencia
              </Button>
              </div>
            </div>

            {/* Modal confirmación Empacado */}
            <ConfirmationModal
              isOpen={showEmpacadoModal}
              onClose={() => setShowEmpacadoModal(false)}
              title="Confirmar Empacado"
              message="¿Está seguro que desea marcar este pedido como Empacado? Se actualizará el estado del pedido."
              type="success"
              confirmText="Confirmar"
              cancelText="Cancelar"
              showCancel
              onConfirm={handleConfirmEmpacado}
            />

            {/* Modal confirmación Incidencia */}
            <ConfirmationModal
              isOpen={showIncidenciaModal}
              onClose={() => setShowIncidenciaModal(false)}
              title="Confirmar Incidencia"
              message="¿Está seguro que desea marcar este pedido con incidencia? Se actualizará el estado del pedido."
              type="warning"
              confirmText="Confirmar"
              cancelText="Cancelar"
              showCancel
              onConfirm={handleConfirmIncidencia}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default DespachosBodega
